import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Dropdown, DropdownItem, DropdownMenu } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query/react';
import classNames from 'classnames';
import dayjs from 'dayjs';

import {
  WeeklyPlannerDateRangeOptions as RangeOptions,
  WeeklyPlannerDatasetOptions as ViewOptions,
} from '~constants/maps';

import { formatDateString, isBeforeDay } from '~libs/dayjs';
import { ReduxSearchOption, useReduxSearchMulti } from '~libs/searchParams';
import { deriveClientLink } from '~appointments/lib/derive';
import { Appointment, deriveTitleFactory, getTimeFromClick } from '~weekly-planner/lib/common';
import { buildAssignChange } from '~weekly-planner/lib/simulate';

import ContainerHeader from '~components/ContainerHeader';
import ContainerPanel from '~components/ContainerPanel';
import { DndWrapper } from '~components/drag-and-drop';
import ErrorResponse from '~components/ErrorResponse';
import Icon, { faUpRightFromSquare } from '~components/icon';
import Link from '~components/Link';
import Banner from '~weekly-planner/components/banner/Banner';
import SettingsModal from '~weekly-planner/components/banner/SettingsModal';
import ClientCell from '~weekly-planner/components/main/ClientCell';
import { View as DailyView } from '~weekly-planner/components/main/daily';
import { View as WeeklyView } from '~weekly-planner/components/main/weekly';
import WorkerCell from '~weekly-planner/components/main/WorkerCell';
import { SideBar } from '~weekly-planner/components/sidebar';
import { AppointmentWizard } from '~weekly-planner/components/wizard/AppointmentWizard';

import clientSearchFields from '~weekly-planner/forms/client/search';
import userSearchFields from '~weekly-planner/forms/user/search';

import { create } from '~appointments/actions/appointments';
import { getAll as getCareQualificationLevels } from '~appointments/actions/careQualificationLevels';
import { getAll as getLocations } from '~appointments/actions/locations';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getUsers } from '~care-workers/actions';
import { getAll as getDepartments } from '~care-workers/actions/userDepartments';
import { getAll as getPayLevels } from '~care-workers/actions/userPayLevels';
import { getAll as getRegions } from '~lookups/actions/regions';
import { getAll as getClients } from '~main/actions/clients';
import { setTitle } from '~main/actions/login';
import { useGetExceptionsQuery, useGetOnHoldQuery } from '~weekly-planner/actions/appointments';
import { useGetAwardAlertTypesQuery } from '~weekly-planner/actions/awardAlerts';
import { useGetClientsQuery } from '~weekly-planner/actions/clients';
import { useGetUsersQuery, useSimulateMutation, usersApi } from '~weekly-planner/actions/users';
import { useGetUsersRunningLateQuery } from '~weekly-planner/actions/users/runningLate';

import { toggleQuickMenu } from '~main/reducers/app';
import { clearSimulated as clearSimulatedClients } from '~weekly-planner/reducers/clients/clients';
import { clear, clearInteracting, makeChange, setInteracting } from '~weekly-planner/reducers/unsaved';
import {
  clearSimulated as clearSimulatedUsers,
  updateParams as updateUserParams,
} from '~weekly-planner/reducers/users/users';
import {
  openAppointmentDetails,
  openAppointmentRosteringNotes,
  patchParams,
  resetWeeklyPlannerState,
  toggleOption,
  update,
} from '~weekly-planner/reducers/weeklyPlanner';

import {
  selectDate,
  selectDerivedDateRange,
  selectFocusedWorkerId,
  selectOptions,
  selectParams,
  selectPlannerAPIOptions,
  selectRangeType,
  selectViewType,
} from '~weekly-planner/selectors';
import {
  selectLoading as selectClientsLoading,
  selectHasLoaded as selectHasClientLoaded,
  selectRenderable as selectSimulatedClientChanges,
} from '~weekly-planner/selectors/clients';
import {
  selectAllErrors,
  selectInteracting,
  selectIsFocusedWorkerInRows,
  selectMergedVisualisedData,
} from '~weekly-planner/selectors/simulate';
import {
  selectHasLoaded as selectHasUserLoaded,
  selectRenderable as selectRenderableWorkers,
  selectParams as selectUserParams,
  selectLoading as selectUsersLoading,
} from '~weekly-planner/selectors/users';

import { useFocusAppointment } from './hooks/useFocusAppointment';
import { selectClientsApiArgs, selectDateRangeArgs, selectUsersApiArgs } from './types/api';

dayjs.updateLocale('en', {
  weekStart: 1,
});

interface AppointmentDefault {
  date: string | null;
  user_id: number | null;
  start_time: string | undefined;
}
const WeeklyPlanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const [simulate] = useSimulateMutation();
  const params = useAppSelector(selectParams);

  const derivedDateRange = useAppSelector(selectDerivedDateRange);
  const focusedWorkerId = useAppSelector(selectFocusedWorkerId);
  const isWorkerInRows = useAppSelector(selectIsFocusedWorkerInRows);
  const interacting = useAppSelector(selectInteracting);
  const {
    appointmentFlexibility,
    workerAvailability,
    workerQualifications,
    workerAwardAlerts,
    totalSimple,
    totalAppointments,
    totalDistance,
    totalPredicted,
    showTravel,
    showBreaks,
    showTeaBreaks,
    showServiceType,
    showCancellations,
    showPayableCancellations,
    showCardQualifications,
    showSystemUsers,
  } = useAppSelector(selectOptions);
  const plannerAPIOptions = useAppSelector(selectPlannerAPIOptions);
  const workerData = useAppSelector(selectMergedVisualisedData);
  const workerDataLoading = useAppSelector(selectUsersLoading);
  const workerDataLoaded = useAppSelector(selectHasUserLoaded);

  const renderableWorkers = useAppSelector(selectRenderableWorkers);
  const clientData = useAppSelector(selectSimulatedClientChanges);
  const clientDataLoading = useAppSelector(selectClientsLoading);
  const clientDataLoaded = useAppSelector(selectHasClientLoaded);
  const userParams = useAppSelector(selectUserParams);
  const weeklyPlannerErrors = useAppSelector(selectAllErrors);

  const [isRightClickMenuOpen, setIsRightClickMenuOpen] = useState(false);
  const [showAppointmentWizard, setShowAppointmentWizard] = useState(false);
  const [appointmentDefaults, setAppointmentDefaults] = useState<AppointmentDefault>({
    date: null,
    user_id: null,
    start_time: undefined,
  });
  const [clickPoint, setClickPoint] = useState({
    x: 0,
    y: 0,
  });

  const { focus_worker_id } = params;
  const { week_start, week_end } = derivedDateRange;

  // Validation functions
  const isValidDate = (value: unknown): value is string => {
    if (typeof value !== 'string') return false;
    const dateParts = value.split(/[- /]/);
    if (dateParts.length !== 3) return false;
    return dayjs(value, ['YYYY-MM-DD', 'YYYY/MM/DD', 'DD/MM/YYYY', 'MM/DD/YYYY'], true).isValid();
  };
  const isValidViewType = (value: unknown): value is (typeof ViewOptions)[keyof typeof ViewOptions] =>
    value === ViewOptions.USER || value === ViewOptions.CLIENT;
  const isValidRangeType = (value: unknown): value is (typeof RangeOptions)[keyof typeof RangeOptions] =>
    value === RangeOptions.WEEKLY || value === RangeOptions.DAILY;

  const urlSpecs = useMemo<ReduxSearchOption[]>(
    () => [
      {
        key: 'date',
        type: 'string',
        validate: isValidDate,
        initialValue: params.date,
        selector: selectDate,
      },
      {
        key: 'viewType',
        type: 'string',
        validate: isValidViewType,
        initialValue: ViewOptions.USER,
        selector: selectViewType,
      },
      {
        key: 'rangeType',
        type: 'string',
        validate: isValidRangeType,
        initialValue: RangeOptions.WEEKLY,
        selector: selectRangeType,
      },
    ],
    [],
  );

  const [reduxSearchValues /* setValues */, , hydrated] = useReduxSearchMulti(urlSpecs, patchParams);

  const { viewType, rangeType } = reduxSearchValues;

  useEffect(() => {
    if (!hydrated || !derivedDateRange) return;
    if (week_start !== derivedDateRange.week_start || week_end !== derivedDateRange.week_end) {
      dispatch(patchParams({ ...derivedDateRange, limit: 30 }));
    }
  }, [hydrated, derivedDateRange, week_start, week_end]);

  const usersArgs = useAppSelector(selectUsersApiArgs);
  const clientsArgs = useAppSelector(selectClientsApiArgs);
  const dateRangeArgs = useAppSelector(selectDateRangeArgs);

  useGetUsersQuery(viewType === 'Users' ? { ...usersArgs, options: plannerAPIOptions } : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  useGetClientsQuery(viewType === 'Clients' ? clientsArgs : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  // never receive search filters:
  useGetExceptionsQuery(dateRangeArgs ?? skipToken);
  useGetOnHoldQuery(dateRangeArgs ?? skipToken);
  useGetUsersRunningLateQuery(dateRangeArgs ?? skipToken);
  useGetAwardAlertTypesQuery();

  const { unfocus: unfocusAppointment } = useFocusAppointment();

  // Unsaved appointments that were just allocated should always allow deallocation.
  // All other cases are disabled when: interacting is not set, or the appointment date is in the past.
  const disableUnassign = React.useMemo(() => {
    // Early guard: disable if not interacting with anything
    if (!interacting) return true;

    // Always enable for unsaved appointments that were just allocated
    if (interacting.key && !interacting.id) return false;

    // Disable when we have an id but no key (not actively interacting with a card)
    if (!interacting.key && !!interacting.id) return true;

    // Disable for past appointments if they have a date
    if (interacting.date && isBeforeDay(dayjs(interacting.date), dayjs())) return true;

    return false;
    // add a stable "today" value so the past-date check re-evaluates across day boundaries
  }, [interacting, dayjs().startOf('day').valueOf()]);
  const disableFindNewWorker = !interacting?.key || viewType === ViewOptions.CLIENT;

  const refresh = () => {
    const workersWithEvents = renderableWorkers.filter(
      (worker) => worker?.events && Object.keys(worker.events).length > 0,
    );

    if (workersWithEvents.length > 0) {
      for (const worker of workersWithEvents) {
        simulate({
          week_start,
          workers: [worker],
          type: 'refresh',
          options: plannerAPIOptions,
        });
      }
    }
  };

  /**
   * Initialization: Set up title, quick menu, fetch static data, and event listeners.
   * Teardown: Remove listeners and reset planner state.
   */
  useEffect(() => {
    setTitle('Weekly Planner');
    dispatch(toggleQuickMenu(true));
    dispatch(getCareQualificationLevels({ attributes: ['id', 'name', 'colour', 'appt_match'] }));
    dispatch(getServiceTypes({ sort: ['name', 'priority'] }));
    dispatch(getUsers({ sort: ['surname'] }));
    dispatch(getRegions());
    dispatch(getDepartments());
    dispatch(getPayLevels());
    dispatch(getClients());
    dispatch(getLocations());
    document.addEventListener('click', leftClickCheck);
    document.addEventListener('contextmenu', contextClick);
    return () => {
      document.removeEventListener('click', leftClickCheck);
      document.removeEventListener('contextmenu', contextClick);
      dispatch(resetWeeklyPlannerState({}));
      dispatch(clearInteracting());
      dispatch(clear());
      dispatch(clearSimulatedUsers([]));
      dispatch(clearSimulatedClients([]));
    };
  }, []);

  /**
   * Update the planner title when week_start changes.
   */
  useEffect(() => {
    if (!hydrated || !week_start) return;
    setTitle(`${formatDateString(week_start)} - Weekly Planner`);
  }, [hydrated, week_start]);

  /**
   * Update params with focusedWorkerId if needed.
   */
  useEffect(() => {
    if (focusedWorkerId !== null && !isWorkerInRows && focus_worker_id !== focusedWorkerId) {
      dispatch(patchParams({ focus_worker_id: focusedWorkerId }));
    }
  }, [focusedWorkerId, isWorkerInRows, focus_worker_id]);

  // updates weekly planner settings and the associated url param
  const changeOptions = (options: Array<{ value: boolean; parameterKey: string; optionKey: string }>) => {
    const params = {
      include: {
        ...userParams.include,
      },
    };

    options.forEach((option) => {
      const { value, parameterKey, optionKey } = option;

      params.include[parameterKey] = value;
      dispatch(toggleOption({ key: optionKey, toggle: value }));
    });

    dispatch(updateUserParams(params));
  };

  const contextClick = (event: MouseEvent) => {
    dispatch(clearInteracting());
    dispatch(openAppointmentDetails(null));

    const clickTarget = event.target as HTMLElement;

    const appointmentCard = clickTarget.closest('.appointment-card');
    const dayCard = clickTarget.closest('.day-cell');
    const dailyViewWorkerRow = clickTarget.closest('.worker-row');

    const workerRow = clickTarget.closest('.daily-cell');
    const isValidContextClick = appointmentCard || dayCard || workerRow;

    if (isValidContextClick) {
      event.preventDefault();
      // TODO lock this in with an id to ensure we get the right div
      const mainWindow = document.getElementsByClassName('feature')[0] as HTMLElement;
      const { x, y } = mainWindow.getBoundingClientRect();

      setClickPoint({ x: event.clientX - x, y: event.clientY - y });
    }

    if (appointmentCard) {
      const key = appointmentCard.getAttribute('id')?.split('-')[2];
      dispatch(setInteracting(key));
      setIsRightClickMenuOpen(true);
    }

    let currentDate = dayCard?.getAttribute('data-date') ?? '';
    let user_id = dayCard?.getAttribute('id') ?? '';
    let time;

    if (workerRow) {
      time = getTimeFromClick(workerRow, event);
      currentDate = dailyViewWorkerRow?.getAttribute('data-date') ?? '';
      user_id = workerRow?.getAttribute('id') ?? '';
    }
    setAppointmentDefaults({
      user_id: parseInt(user_id),
      date: currentDate,
      start_time: time?.toString(),
    });

    setIsRightClickMenuOpen(true);

    //context menu should close when we right click off the WP (mimics browser context menu)
    if (!dayCard && !appointmentCard && !workerRow) {
      setIsRightClickMenuOpen(false);
    }
  };

  const leftClickCheck = (event: MouseEvent) => {
    const popover = (event.target as HTMLElement)?.closest('.appointment-popover');
    const dropdown = document.getElementById('weekly-planner-context') as HTMLElement;

    // Dont reset context if the click is on an appointment popover or the context menu
    if (popover || dropdown?.contains(event.target as Node)) {
      return;
    }

    dispatch(clearInteracting());
    dispatch(openAppointmentDetails(null));
    dispatch(openAppointmentRosteringNotes(null));
    setIsRightClickMenuOpen(false);
  };

  const { x: left, y: top } = clickPoint;

  const dropdownStyle: CSSProperties = { top, left, position: 'absolute' };

  const MenuLink = ({ to, children, disabled = false }: { to: string; children: ReactNode; disabled?: boolean }) => (
    <Link to={to} target="_blank" className="text-decoration-none" disabled={disabled}>
      <DropdownItem disabled={disabled}>
        {children}
        <Icon icon={faUpRightFromSquare} className="hover-icon" />
      </DropdownItem>
    </Link>
  );

  const unassignAppointment = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    if (interacting) {
      const change = buildAssignChange(data, [interacting], true);

      const { id, parent_repeat_id } = interacting;
      dispatch(makeChange({ original: interacting, change: { id, parent_repeat_id, user_id: null } }));

      simulate({ ...change, week_start, options: plannerAPIOptions });
    }
  };

  const addNewAppointment = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    dispatch(getClients());
    dispatch(getUsers());
    dispatch(
      getServiceTypes({
        deleted: { or: [true, false] },
        sort: ['deleted', 'name'],
      }),
    );
    setShowAppointmentWizard(true);
  };

  const findNewWorker = () => {
    unfocusAppointment();
    dispatch(update({ focusedWorkerId: interacting?.user_id }));
    dispatch(patchParams({ focus_worker_id: interacting?.user_id }));
  };

  const addAppointment = async (values: any) => {
    const { payload } = await dispatch(create(values));

    if (payload?.success) {
      setShowAppointmentWizard(false);
      setAppointmentDefaults({
        date: null,
        user_id: null,
        start_time: undefined,
      });
      dispatch(usersApi.util.invalidateTags([{ type: 'users', id: 'LIST' }]));
    }

    return payload?.success;
  };

  const isDaily = rangeType === RangeOptions.DAILY;
  const isUser = viewType === ViewOptions.USER;

  const dataLoadingState = isUser ? workerDataLoading : clientDataLoading;
  const dataHasLoaded = isUser ? workerDataLoaded : clientDataLoaded;

  const data = isUser ? workerData : clientData;
  const headerCell = isUser ? WorkerCell : ClientCell;
  const searchFields = isUser ? userSearchFields : clientSearchFields;
  const containerClassName = classNames('main-view', {
    focused: focusedWorkerId !== null,
  });

  const errors = weeklyPlannerErrors.filter((value, index, array) => array.indexOf(value) === index);

  const getAppointmentTitle = deriveTitleFactory<Appointment, 'user' | 'client'>(isUser ? 'client' : 'user');

  const headerTitle = isUser ? 'Care Worker' : 'Client';

  return (
    <DndWrapper canScroll={!focusedWorkerId}>
      <div className="p-2 weekly-planner">
        <ContainerHeader
          settingsElement={
            <SettingsModal
              showCancellations={showCancellations}
              changeOptions={changeOptions}
              showPayableCancellations={showPayableCancellations}
              workerQualifications={workerQualifications}
              dispatch={dispatch}
              workerAvailability={workerAvailability}
              viewType={viewType}
              appointmentFlexibility={appointmentFlexibility}
              showSystemUsers={showSystemUsers}
              showServiceType={showServiceType}
              showBreaks={showBreaks}
              showTeaBreaks={showTeaBreaks}
              showTravel={showTravel}
              workerAwardAlerts={workerAwardAlerts}
              totalSimple={totalSimple}
              totalAppointments={totalAppointments}
              showCardQualifications={showCardQualifications}
              totalPredicted={totalPredicted}
              totalDistance={totalDistance}
              refresh={refresh}
            />
          }
        >
          Weekly Planner
        </ContainerHeader>
        {errors.map((error, index) => (
          <ErrorResponse key={index} message={error} dataCy="weeklyPlanner-errorResponse" />
        ))}

        <Banner searchFields={searchFields} loadingState={dataLoadingState} />
        <ContainerPanel className={containerClassName} sidePanel={<SideBar />}>
          {!isDaily ? (
            <WeeklyView
              headerCell={headerCell}
              rows={dataHasLoaded ? data : []}
              focusId={focusedWorkerId}
              loadingState={dataLoadingState}
              getAppointmentTitle={getAppointmentTitle}
              headerTitle={headerTitle}
            />
          ) : (
            <DailyView
              headerCell={headerCell}
              rows={dataHasLoaded ? data : []}
              focusId={focusedWorkerId}
              loadingState={dataLoadingState}
              getAppointmentTitle={getAppointmentTitle}
            />
          )}
        </ContainerPanel>
        <Dropdown
          id="weekly-planner-context"
          style={dropdownStyle}
          isOpen={isRightClickMenuOpen}
          toggle={() => setIsRightClickMenuOpen(!isRightClickMenuOpen)}
          className="weekly-planner-context"
        >
          <DropdownMenu>
            <DropdownItem
              disabled={!interacting?.id && !interacting?.key}
              onClick={(event) => {
                event.stopPropagation();
                dispatch(openAppointmentRosteringNotes(null));
                dispatch(openAppointmentDetails(interacting?.key || interacting?.id));
                dispatch(clearInteracting());
              }}
            >
              View Details
            </DropdownItem>
            <DropdownItem
              disabled={!interacting?.key}
              onClick={(event) => {
                event.stopPropagation();
                dispatch(openAppointmentDetails(null));
                dispatch(openAppointmentRosteringNotes(interacting?.key || interacting?.id));
                dispatch(clearInteracting());
              }}
            >
              Add Rostering Note
            </DropdownItem>
            <DropdownItem disabled={disableUnassign} onClick={unassignAppointment}>
              Unassign
            </DropdownItem>
            <DropdownItem disabled={disableFindNewWorker} onClick={findNewWorker}>
              Focus Worker
            </DropdownItem>
            <DropdownItem divider></DropdownItem>
            <DropdownItem onClick={addNewAppointment}>Create Appointment</DropdownItem>
            <DropdownItem divider></DropdownItem>
            <MenuLink disabled={!interacting?.id} to={`${window.origin}/appointments/${interacting?.id}`}>
              View Appointment
            </MenuLink>
            <MenuLink
              disabled={!interacting?.parent_repeat_id}
              to={`${window.origin}/appointments/${interacting?.parent_repeat_id}`}
            >
              View Parent
            </MenuLink>
            <MenuLink
              disabled={!interacting?.client_id && !interacting?.client?.id}
              to={deriveClientLink({ client_id: interacting?.client_id ?? interacting?.client?.id })}
            >
              View Client
            </MenuLink>
          </DropdownMenu>
        </Dropdown>
        <AppointmentWizard
          showAppointmentWizard={showAppointmentWizard}
          setShowAppointmentWizard={setShowAppointmentWizard}
          appointmentDefaults={appointmentDefaults}
          onAddAppointment={addAppointment}
        />
      </div>
    </DndWrapper>
  );
};

export default WeeklyPlanner;
